import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserPreferences } from '@app/shared/models/user-preferences.model';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ENDPOINTS } from '../constants/endpoints';
import { AUTHENTICATION_TOKEN_KEY, METRICS_KEY } from '../constants/storage-keys';
import { UserPreferencesAdapter } from '../services/adapters/user-preferences.adapter';
import { EncryptingService } from '../services/encrypting.service';
import { CookieService } from 'ngx-cookie-service';

@Injectable()
export class UserPreferencesStore {
    private userPreferencesSubject: BehaviorSubject<UserPreferences>;
    private httpSkippingInterceptors: HttpClient;

    constructor(
        private httpBackend: HttpBackend,
        private userPreferencesAdapter: UserPreferencesAdapter,
        private encryptingService: EncryptingService,
        private cookieService: CookieService
    ) {
        this.httpSkippingInterceptors = new HttpClient(this.httpBackend);
        this.userPreferencesSubject = new BehaviorSubject<UserPreferences>(undefined);
    }

    get userPreferences$(): Observable<UserPreferences> {
        return this.userPreferencesSubject.asObservable();
    }

    get userPreferences(): UserPreferences {
        return this.userPreferencesSubject.value;
    }

    get userPreferenceTimeFormat(): string {
        return this.userPreferences.use24HourClock ? 'kk:mm' : 'hh:mm A';
    }

    get userPreferenceTimeFormatWithSeconds(): string {
        return this.userPreferences.use24HourClock ? 'kk:mm:ss' : 'hh:mm:ss A';
    }

    async initialize(userId: number) {
        let preferences: UserPreferences;
        try {
            preferences = await lastValueFrom(this.getUserPreferences(userId));
        } catch {
            preferences = {
                use24HourClock: true,
                useMetricUnits: true,
            };
        }

        let cookieData = {
            temperature: '2',
            time: '1',
        };

        try {
            cookieData = {
                temperature: preferences.useMetricUnits ? '2' : '1',
                time: preferences.use24HourClock ? '1' : '2',
            };
            /* eslint-disable-next-line no-empty */
        } catch {}

        this.cookieService.set(METRICS_KEY, JSON.stringify(cookieData), 365, '/');
        this.userPreferencesSubject.next(preferences);
    }

    clear() {
        this.userPreferencesSubject.next(undefined);
    }

    private getUserPreferences(userId: number): Observable<UserPreferences> {
        const storedData = localStorage.getItem(AUTHENTICATION_TOKEN_KEY);
        const rawData = this.encryptingService.decrypt(storedData);
        const headers = new HttpHeaders().set('x-access-token', rawData['token']);

        return this.httpSkippingInterceptors
            .get(`${ENDPOINTS.apis.users.base}/${userId}/preferences`, { headers })
            .pipe(map((data) => this.userPreferencesAdapter.adapt(data)));
    }
}
